import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  UserCheck, Search, Building2, Phone, Mail, MapPin, Clock,
  ArrowUpDown, ArrowUp, ArrowDown, ChevronDown, ChevronRight, Loader2, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../utils/config';

const EMPTY_FILTERS = { search: '', zone: '', propRegion: '', propProvince: '', propCity: '', minProps: '', activeOnly: '', partner: '' };

export default function ResponsabiliRubrica() {
  const token = localStorage.getItem('hw_token');
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/tickets/managers-list`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => setManagers(Array.isArray(data) ? data : []))
      .catch(() => toast.error('Errore nel caricamento dei responsabili'))
      .finally(() => setLoading(false));
  }, []);

  const options = useMemo(() => {
    const zones = new Set(), cities = new Set(), provinces = new Set(), regions = new Set();
    managers.forEach(m => {
      if (m.city) zones.add(m.city);
      if (m.operation_area_city) zones.add(m.operation_area_city);
      (m.property_cities || []).forEach(c => cities.add(c));
      (m.property_provinces || []).forEach(p => provinces.add(p));
      (m.property_regions || []).forEach(r => regions.add(r));
    });
    const s = set => [...set].sort();
    return { zones: s(zones), cities: s(cities), provinces: s(provinces), regions: s(regions) };
  }, [managers]);

  const filtered = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    let result = managers.filter(m => {
      if (q && !(`${m.name} ${m.email} ${m.phone}`.toLowerCase().includes(q))) return false;
      if (filters.zone && m.city !== filters.zone && m.operation_area_city !== filters.zone) return false;
      if (filters.propRegion && !(m.property_regions || []).includes(filters.propRegion)) return false;
      if (filters.propProvince && !(m.property_provinces || []).includes(filters.propProvince)) return false;
      if (filters.propCity && !(m.property_cities || []).includes(filters.propCity)) return false;
      if (filters.minProps !== '') {
        if (filters.minProps === '0' && m.property_count !== 0) return false;
        if (filters.minProps !== '0' && m.property_count < Number(filters.minProps)) return false;
      }
      if (filters.activeOnly === 'si' && m.active_property_count === 0) return false;
      if (filters.activeOnly === 'no' && m.active_property_count > 0) return false;
      if (filters.partner === 'si' && !m.is_also_partner) return false;
      if (filters.partner === 'no' && m.is_also_partner) return false;
      return true;
    });
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    const val = (m) => {
      switch (sortConfig.key) {
        case 'name': return (m.name || '').toLowerCase();
        case 'email': return (m.email || '').toLowerCase();
        case 'zone': return (m.operation_area_city || m.city || '').toLowerCase();
        case 'props': return m.property_count || 0;
        case 'active': return m.active_property_count || 0;
        case 'partner': return m.is_also_partner ? 1 : 0;
        default: return '';
      }
    };
    result.sort((a, b) => {
      const va = val(a), vb = val(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return result;
  }, [managers, filters, sortConfig]);

  const activeUsers = useMemo(() => filtered.filter(m => m.uses_hwsmart), [filtered]);
  const pendingUsers = useMemo(() => filtered.filter(m => !m.uses_hwsmart), [filtered]);
  const totActiveUsers = managers.filter(m => m.uses_hwsmart).length;

  const handleSort = (key) => setSortConfig(prev => ({
    key,
    direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
  }));

  const sortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="h-3.5 w-3.5 text-slate-300" />;
    return sortConfig.direction === 'asc' ? <ArrowUp className="h-3.5 w-3.5 text-purple-600" /> : <ArrowDown className="h-3.5 w-3.5 text-purple-600" />;
  };

  const Th = ({ k, children, className = '' }) => (
    <th className={`px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase cursor-pointer select-none hover:bg-slate-100 ${className}`}
      onClick={() => handleSort(k)} data-testid={`rubrica-sort-${k}`}>
      <span className="flex items-center gap-1">{children} {sortIcon(k)}</span>
    </th>
  );

  const renderTable = (list, tableId, emptyMsg) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid={tableId}>
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="w-8"></th>
            <Th k="name">Nominativo</Th>
            <Th k="email">Contatti</Th>
            <Th k="zone" className="hidden md:table-cell">Zona operativa</Th>
            <Th k="active">Immobili attivi</Th>
            <Th k="props" className="hidden lg:table-cell">Totali</Th>
            <Th k="partner" className="hidden lg:table-cell">Partner</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {list.length === 0 ? (
            <tr><td colSpan="7" className="p-6 text-center text-slate-400">{emptyMsg}</td></tr>
          ) : list.map(m => (
            <React.Fragment key={m.id}>
              <tr className="hover:bg-purple-50/40 cursor-pointer" onClick={() => setExpanded(expanded === m.id ? null : m.id)} data-testid={`rubrica-row-${m.id}`}>
                <td className="pl-3">
                  {expanded === m.id ? <ChevronDown className="h-4 w-4 text-purple-500" /> : <ChevronRight className="h-4 w-4 text-slate-300" />}
                </td>
                <td className="px-3 py-2.5">
                  <span className="font-medium text-slate-800">{m.name}</span>
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-1.5 text-slate-600 text-xs">
                    <Mail className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    <span className="truncate max-w-[200px]">{m.email || '—'}</span>
                  </div>
                  {m.phone && (
                    <div className="flex items-center gap-1.5 text-slate-500 text-xs mt-0.5">
                      <Phone className="h-3.5 w-3.5 text-slate-400 shrink-0" />{m.phone}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2.5 hidden md:table-cell">
                  <div className="flex items-center gap-1.5 text-slate-600 text-xs">
                    <MapPin className="h-3.5 w-3.5 text-slate-400" />
                    {m.operation_area_city || m.city || '—'}{m.province ? ` (${m.province})` : ''}
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${m.active_property_count > 0 ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                    <Building2 className="h-3 w-3" />{m.active_property_count}
                  </span>
                </td>
                <td className="px-3 py-2.5 hidden lg:table-cell text-slate-500">{m.property_count}</td>
                <td className="px-3 py-2.5 hidden lg:table-cell">
                  {m.is_also_partner ? <span className="text-[10px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">Partner</span> : <span className="text-slate-300">—</span>}
                </td>
              </tr>
              {expanded === m.id && (
                <tr className="bg-slate-50/70" data-testid={`rubrica-detail-${m.id}`}>
                  <td></td>
                  <td colSpan="6" className="px-3 py-3">
                    {m.uses_hwsmart && m.account_activated_at && (
                      <p className="text-xs text-green-600 mb-2">
                        <strong>Account HW Smart attivato il:</strong> {new Date(m.account_activated_at).toLocaleDateString('it-IT')}
                      </p>
                    )}
                    {m.property_cities?.length > 0 && (
                      <p className="text-xs text-slate-500 mb-2">
                        <strong>Città immobili:</strong> {m.property_cities.join(', ')}
                      </p>
                    )}
                    {(m.properties || []).length === 0 ? (
                      <p className="text-xs text-slate-400">Nessun immobile assegnato</p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1.5">
                        {m.properties.map((p, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs bg-white border border-slate-200 rounded px-2 py-1.5">
                            <Building2 className={`h-3.5 w-3.5 shrink-0 ${p.status === 'active' ? 'text-green-500' : 'text-slate-300'}`} />
                            <span className="truncate flex-1 text-slate-700">{p.name}</span>
                            {p.city && <span className="text-slate-400 shrink-0">{p.city}</span>}
                            <span className={`shrink-0 px-1.5 py-0.5 rounded-full text-[10px] ${p.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-400'}`}>
                              {p.status === 'active' ? 'attivo' : 'cessato'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );

  const selClass = "p-2 border border-slate-300 rounded-lg text-sm bg-white";
  const totActive = managers.reduce((s, m) => s + (m.active_property_count || 0), 0);

  return (
    <Layout>
      <div className="space-y-5" data-testid="responsabili-rubrica-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <UserCheck className="h-6 w-6 text-purple-600" />
              Responsabili
            </h1>
            <p className="text-slate-500">Rubrica responsabili immobili: contatti, zone e immobili gestiti</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Card className="px-4 py-2 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <div>
                <p className="text-lg font-bold text-slate-800 leading-tight" data-testid="rubrica-hwsmart-count">{totActiveUsers}</p>
                <p className="text-[11px] text-slate-400">Usano HW Smart</p>
              </div>
            </Card>
            <Card className="px-4 py-2 flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-500" />
              <div>
                <p className="text-lg font-bold text-slate-800 leading-tight" data-testid="rubrica-pending-count">{managers.length - totActiveUsers}</p>
                <p className="text-[11px] text-slate-400">Primo accesso da fare</p>
              </div>
            </Card>
            <Card className="px-4 py-2 flex items-center gap-2">
              <Building2 className="h-4 w-4 text-purple-500" />
              <div>
                <p className="text-lg font-bold text-slate-800 leading-tight">{totActive}</p>
                <p className="text-[11px] text-slate-400">Immobili attivi gestiti</p>
              </div>
            </Card>
          </div>
        </div>

        {/* Filtri */}
        <Card className="p-3 space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
              placeholder="Cerca per nome, email o telefono..." className="pl-10" data-testid="rubrica-search" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
            <select value={filters.zone} onChange={e => setFilters(f => ({ ...f, zone: e.target.value }))} className={selClass} data-testid="rubrica-filter-zone">
              <option value="">Zona operativa: tutte</option>
              {options.zones.map(z => <option key={z} value={z}>{z}</option>)}
            </select>
            <select value={filters.propRegion} onChange={e => setFilters(f => ({ ...f, propRegion: e.target.value }))} className={selClass} data-testid="rubrica-filter-region">
              <option value="">Regione immobili: tutte</option>
              {options.regions.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <select value={filters.propProvince} onChange={e => setFilters(f => ({ ...f, propProvince: e.target.value }))} className={selClass} data-testid="rubrica-filter-province">
              <option value="">Provincia: tutte</option>
              {options.provinces.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <select value={filters.propCity} onChange={e => setFilters(f => ({ ...f, propCity: e.target.value }))} className={selClass} data-testid="rubrica-filter-city">
              <option value="">Città immobili: tutte</option>
              {options.cities.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={filters.minProps} onChange={e => setFilters(f => ({ ...f, minProps: e.target.value }))} className={selClass} data-testid="rubrica-filter-minprops">
              <option value="">N. immobili: tutti</option>
              <option value="0">Senza immobili</option>
              <option value="1">Almeno 1</option>
              <option value="3">Almeno 3</option>
              <option value="5">Almeno 5</option>
              <option value="10">Almeno 10</option>
            </select>
            <select value={filters.activeOnly} onChange={e => setFilters(f => ({ ...f, activeOnly: e.target.value }))} className={selClass} data-testid="rubrica-filter-active">
              <option value="">Immobili attivi: tutti</option>
              <option value="si">Con immobili attivi</option>
              <option value="no">Senza immobili attivi</option>
            </select>
            <select value={filters.partner} onChange={e => setFilters(f => ({ ...f, partner: e.target.value }))} className={selClass} data-testid="rubrica-filter-partner">
              <option value="">Anche partner: tutti</option>
              <option value="si">Solo partner</option>
              <option value="no">Solo non partner</option>
            </select>
            <button onClick={() => setFilters(EMPTY_FILTERS)}
              className="text-sm text-purple-600 hover:text-purple-800 border border-purple-200 rounded-lg bg-white" data-testid="rubrica-filter-reset">
              Azzera filtri
            </button>
          </div>
          <p className="text-xs text-slate-400" data-testid="rubrica-filtered-count">{filtered.length} responsabili visualizzati ({activeUsers.length} attivi su HW Smart · {pendingUsers.length} in attesa di primo accesso)</p>
        </Card>

        {loading ? (
          <Card className="p-10 flex items-center justify-center gap-2 text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" /> Caricamento...
          </Card>
        ) : (
          <>
            {/* Tabella 1: usano HW Smart */}
            <div data-testid="rubrica-section-active">
              <h2 className="text-base font-semibold text-slate-700 flex items-center gap-2 mb-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                Usano già HW Smart
                <span className="text-xs font-normal bg-green-100 text-green-700 px-2 py-0.5 rounded-full">{activeUsers.length}</span>
              </h2>
              <Card className="overflow-hidden border-l-4 border-l-green-400">
                {renderTable(activeUsers, 'rubrica-table-active', 'Nessun responsabile ha ancora attivato HW Smart (con i filtri attuali)')}
              </Card>
            </div>

            {/* Tabella 2: primo accesso non effettuato */}
            <div data-testid="rubrica-section-pending">
              <h2 className="text-base font-semibold text-slate-700 flex items-center gap-2 mb-2">
                <Clock className="h-5 w-5 text-amber-500" />
                Primo accesso non ancora effettuato
                <span className="text-xs font-normal bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{pendingUsers.length}</span>
              </h2>
              <Card className="overflow-hidden border-l-4 border-l-amber-400">
                {renderTable(pendingUsers, 'rubrica-table-pending', 'Tutti i responsabili filtrati hanno già effettuato il primo accesso')}
              </Card>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
